import { Code, User, Github, Star } from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "../theme/ThemeContext";
import { useEffect, useState } from "react";
import { theme } from "@/theme/theme";

const Skills = () => {
  const { theme: themeMode } = useTheme();
  const isDark = themeMode === 'dark';
  const skillsStyles = theme.skills[themeMode];

  const skillCategories = [
    {
      title: "Frontend Technologies",
      icon: <Code className="w-6 h-6" />,
      skills: ["React.js", "Vue.js", "TypeScript", "Next.js", "Tailwind CSS"]
    },
    {
      title: "Animation & 3D",
      icon: <Star className="w-6 h-6" />,
      skills: ["Framer Motion", "GSAP", "Three.js", "React Three Fiber", "CSS Animations"]
    },
    {
      title: "Backend & Database",
      icon: <Github className="w-6 h-6" />,
      skills: ["Node.js", "Express.js", "MongoDB", "REST APIs", "Mongoose"]
    },
    {
      title: "Development Tools",
      icon: <User className="w-6 h-6" />,
      skills: ["Git", "Figma", "shadcn/ui", "Radix UI", "Vuetify"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const skillVariants = {
    hidden: {
      x: -40,
      opacity: 0,
      scale: 0.9,
      filter: "blur(4px)"
    },
    visible: (i: number) => ({
      x: 0,
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.12,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }),
    hover: {
      x: 8,
      scale: 1.03,
      filter: "brightness(1.1)",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15
      }
    }
  };

  // Dynamic color values based on theme
  const initialTextColor = isDark ? "#fef3c7" : "#1f2937";
  const hoverTextColor = isDark ? "#000000" : "#ffffff";
  const initialIconColor = skillsStyles.icon.color;
  const hoverIconColor = skillsStyles.icon.hoverColor;

  const headerHoverVariants = {
    initial: {
      scale: 1,
      color: initialTextColor,
      y: 0,
      filter: "blur(0px)"
    },
    hover: {
      scale: 1.01,
      y: -2,
      color: hoverTextColor,
      filter: "blur(0px)",
      transition: {
        type: "spring",
        stiffness: 250,
        damping: 20,
        duration: 0.3
      }
    }
  };

  const iconHoverVariants = {
    initial: {
      scale: 1,
      rotate: 0,
      color: initialIconColor,
      y: 0,
      filter: "blur(0px)"
    },
    hover: {
      scale: 1.15,
      rotate: 8,
      y: -3,
      color: hoverIconColor,
      filter: "blur(0px)",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
        duration: 0.3
      }
    }
  };

  const cardVariants = {
    initial: {
      scale: 1
    },
    hover: {
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        duration: 0.3
      }
    }
  };

  const iconVariants = {
    initial: {
      scale: 1,
      rotate: 0,
      color: initialIconColor,
      y: 0,
      filter: "blur(0px)"
    },
    hover: {
      scale: 1.15,
      rotate: 8,
      y: -3,
      color: hoverIconColor,
      filter: "blur(0px)",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
        duration: 0.3
      }
    }
  };

  const headerIconVariants = {
    initial: {
      scale: 1,
      rotate: 0,
      color: initialIconColor,
      y: 0,
      filter: "blur(0px)"
    },
    hover: {
      scale: 1.15,
      rotate: 8,
      y: -3,
      color: hoverIconColor,
      filter: "blur(0px)",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
        duration: 0.3
      }
    }
  };

  // Force re-render of motion components when theme changes
  const [key, setKey] = useState(0);
  useEffect(() => {
    setKey(prev => prev + 1);
  }, [themeMode]);

  return (
    <section id="skills" className={`py-24 px-4 sm:px-6 lg:px-8 relative ${skillsStyles.background}`}>
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
          className="text-center mb-8 sm:mb-12 md:mb-16"
        >
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 transition-colors duration-500 font-mono ${skillsStyles.text.main}`}>
            Technical Expertise
          </h2>
          <motion.div
            className={`w-16 sm:w-20 h-1 mx-auto rounded-full ${isDark ? 'bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400' : 'bg-gradient-to-r from-teal-400 via-teal-500 to-teal-400'} transition-colors duration-500`}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ duration: 0.7, ease: 'circOut' }}
            style={{ originX: 0.5 }}
          />
        </motion.div>

        <motion.div
          key={key}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {skillCategories.map((category, index) => (
            <motion.div
              key={`${key}-${index}`}
              variants={cardVariants}
              initial="initial"
              whileHover="hover"
              className="group perspective-1000"
            >
              <div className={`
                ${skillsStyles.card.background}
                ${skillsStyles.card.hover}
                border backdrop-blur-sm rounded-2xl p-8 
                hover:border-teal-500/50 hover:shadow-2xl
                transition-all duration-500 shadow-lg 
                relative overflow-hidden
                ${skillsStyles.card.shadow}
                ${skillsStyles.card.hoverShadow}
                transform-gpu
                will-change-transform
                h-[320px] flex flex-col
              `}>
                <div className={`absolute inset-0 bg-gradient-to-r ${skillsStyles.card.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out`} />
                <div className={`
                  absolute inset-0 
                  ${isDark ? 'bg-gradient-to-br from-white/5' : 'bg-gradient-to-br from-teal-500/5'} 
                  to-transparent opacity-0 group-hover:opacity-100 
                  transition-opacity duration-500 ease-out
                `} />

                <div className="flex items-center mb-8 relative z-10">
                  <motion.div
                    key={`${key}-icon-${index}`}
                    variants={headerIconVariants}
                    className={`
                      ${skillsStyles.text.accent} ${skillsStyles.text.hover} ${skillsStyles.icon.shadow} mr-4 
                      transform
                      transition-all duration-500 ease-out
                    `}
                  >
                    {category.icon}
                  </motion.div>
                  <motion.h3
                    key={`${key}-title-${index}`}
                    variants={headerHoverVariants}
                    initial="initial"
                    whileHover="hover"
                    className={`
                      text-xl font-mono font-semibold tracking-tight
                      ${skillsStyles.text.main} ${skillsStyles.text.hover}
                    `}
                  >
                    {category.title}
                  </motion.h3>
                </div>
                <ul className="space-y-4 relative z-10 flex-grow">
                  {category.skills.map((skill, skillIndex) => (
                    <motion.li
                      key={`${key}-skill-${index}-${skillIndex}`}
                      custom={skillIndex}
                      variants={skillVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover="hover"
                      className={`
                        ${skillsStyles.text.main} ${skillsStyles.text.hover}
                        text-sm font-mono font-medium tracking-wide
                        flex items-center
                        cursor-default
                      `}
                    >
                      <motion.span
                        key={`${key}-dot-${index}-${skillIndex}`}
                        variants={iconVariants}
                        className={`
                          w-1.5 h-1.5 rounded-full 
                          ${skillsStyles.text.accent} ${skillsStyles.text.hover}
                          mr-3
                          transform
                          transition-all duration-500 ease-out
                        `}
                      />
                      {skill}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
